import * as React from "react";
import { cn } from "../../utils";
import {
  autoSizeTextarea,
  columnIndexToName,
  getCellDisplayValue,
  getCellLabel,
  isCellSelected,
} from "./model";
import {
  DEFAULT_ROW_HEIGHT,
  ROW_HEADER_WIDTH,
  type Coord,
  type ResizingState,
  type SelectionState,
  type TableCellModel,
} from "./types";

type TableGridProps = {
  columnWidths: number[];
  editingCell: Coord | null;
  fixedCol?: number;
  fixedRow?: number;
  getColumnStickyOffset: (colIndex: number) => number;
  getRowStickyOffset: (rowIndex: number) => number;
  hoveredResizeCol: number | null;
  onCellTextChange: (value: string) => void;
  onSelectCell: (row: number, col: number, append?: boolean) => void;
  onSetEditingCell: (cell: Coord | null) => void;
  onSetHoveredResizeCol: (colIndex: number | null) => void;
  onSetSelection: (selection: SelectionState) => void;
  onStartColumnResize: (event: React.PointerEvent, colIndex: number) => void;
  resizing: ResizingState;
  rowHeights: number[];
  rowRefs: React.MutableRefObject<Array<HTMLTableRowElement | null>>;
  selection: SelectionState;
  table: TableCellModel[][];
  totalWidth: number;
};

export function TableGrid({
  columnWidths,
  editingCell,
  fixedCol,
  fixedRow,
  getColumnStickyOffset,
  getRowStickyOffset,
  hoveredResizeCol,
  onCellTextChange,
  onSelectCell,
  onSetEditingCell,
  onSetHoveredResizeCol,
  onSetSelection,
  onStartColumnResize,
  resizing,
  rowHeights,
  rowRefs,
  selection,
  table,
  totalWidth,
}: TableGridProps) {
  return (
    <div className="max-h-[40vh] overflow-auto rounded border border-gray-200 bg-white">
      <div
        className="inline-grid"
        style={{
          gridTemplateColumns: `${ROW_HEADER_WIDTH}px ${totalWidth}px`,
        }}
      >
        <div className="sticky left-0 top-0 z-40 border-b border-r border-gray-300 bg-gray-200" />
        <div
          className="sticky top-0 z-30 grid"
          style={{
            gridTemplateColumns: columnWidths
              .map((width) => `${width}px`)
              .join(" "),
          }}
        >
          {columnWidths.map((width, index) => (
            <div
              key={`column-header-${index}`}
              className="select-none border-b border-r border-gray-300 bg-gray-200 px-3 py-2 text-center text-xs font-bold text-gray-600"
              style={{ width }}
            >
              {columnIndexToName(index)}
            </div>
          ))}
        </div>
        <div className="sticky left-0 z-30 bg-gray-200">
          {table.map((_, rowIndex) => (
            <div
              key={`row-header-${rowIndex}`}
              className="flex select-none items-center justify-center border-b border-r border-gray-300 px-2 text-xs font-bold text-gray-600"
              style={{ height: rowHeights[rowIndex] ?? DEFAULT_ROW_HEIGHT }}
            >
              {rowIndex + 1}
            </div>
          ))}
        </div>
        <table
          className="table-fixed select-none border-collapse text-left text-sm"
          style={{ width: totalWidth }}
        >
          <colgroup>
            {columnWidths.map((width, index) => (
              <col key={`col-${index}`} style={{ width }} />
            ))}
          </colgroup>
          <tbody>
            {table.map((row, rowIndex) => (
              <tr
                key={`row-${rowIndex}`}
                ref={(node) => {
                  rowRefs.current[rowIndex] = node;
                }}
              >
                {row.map((cell, colIndex) => {
                  if (cell.hidden) return null;
                  const Tag = cell.type === "header" ? "th" : "td";
                  const selected = isCellSelected(rowIndex, colIndex, selection);
                  const isEditing =
                    editingCell?.row === rowIndex && editingCell.col === colIndex;
                  const isFrozenRow =
                    fixedRow !== undefined && rowIndex < fixedRow;
                  const isFrozenCol =
                    fixedCol !== undefined && colIndex < fixedCol;
                  const isFreezeRowBoundary =
                    fixedRow !== undefined && fixedRow > 0 && rowIndex === fixedRow - 1;
                  const isFreezeColBoundary =
                    fixedCol !== undefined && fixedCol > 0 && colIndex === fixedCol - 1;
                  const displayValue = getCellDisplayValue(table, cell);
                  const isFormula = String(cell.text).trim().startsWith("=");
                  return (
                    <Tag
                      key={cell.id}
                      title={getCellLabel(rowIndex, colIndex)}
                      rowSpan={cell.rowspan}
                      colSpan={cell.colspan}
                      onMouseDown={(event) => {
                        if (!isEditing) event.preventDefault();
                      }}
                      onClick={(event) => onSelectCell(rowIndex, colIndex, event.shiftKey)}
                      onDoubleClick={(event) => {
                        event.preventDefault();
                        onSetSelection({ start: { row: rowIndex, col: colIndex }, end: { row: rowIndex, col: colIndex } });
                        onSetEditingCell({ row: rowIndex, col: colIndex });
                      }}
                      className={cn(
                        "group relative min-w-[80px] cursor-pointer border border-gray-300 align-top transition",
                        cell.type === "header" || cell.bold
                          ? "font-semibold"
                          : "font-normal",
                        cell.italic && "italic",
                        cell.type === "header" || isFrozenRow || isFrozenCol
                          ? "bg-gray-100"
                          : "bg-white",
                        selected && "ring-2 ring-inset ring-pinkCustom",
                        (hoveredResizeCol === colIndex || resizing?.colIndex === colIndex) && "border-r-2 border-r-blue-500",
                        !isEditing ? "p-3" : "p-1",
                      )}
                      style={{
                        borderBottom: isFreezeRowBoundary ? "3px solid rgb(34 197 94)" : undefined,
                        borderRight: isFreezeColBoundary ? "3px solid rgb(34 197 94)" : undefined,
                        direction: cell.direction,
                        left: isFrozenCol ? getColumnStickyOffset(colIndex) : undefined,
                        position: isFrozenRow || isFrozenCol ? "sticky" : undefined,
                        textAlign: cell.align,
                        top: isFrozenRow ? getRowStickyOffset(rowIndex) : undefined,
                        zIndex:
                          isFrozenRow && isFrozenCol
                            ? 30
                            : isFrozenRow
                              ? 20
                              : isFrozenCol
                                ? 10
                                : undefined,
                      }}
                    >
                      {isEditing ? (
                        <textarea
                          autoFocus
                          rows={1}
                          value={cell.text}
                          ref={autoSizeTextarea}
                          onInput={(event) => autoSizeTextarea(event.currentTarget)}
                          onChange={(event) => onCellTextChange(event.target.value)}
                          onClick={(event) => event.stopPropagation()}
                          onDoubleClick={(event) => event.stopPropagation()}
                          onBlur={() => onSetEditingCell(null)}
                          onKeyDown={(event) => {
                            if (event.key === "Escape") {
                              event.preventDefault();
                              onSetEditingCell(null);
                            }
                          }}
                          className="min-h-8 w-full resize-none overflow-hidden px-2 py-1 text-sm font-normal leading-5 outline-none"
                        />
                      ) : (
                        <div className="whitespace-pre-wrap break-words pr-2 leading-5">
                          {displayValue || <span className="text-gray-400">Empty</span>}
                          {isFormula ? (
                            <span className="ml-2 rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">
                              fx
                            </span>
                          ) : null}
                        </div>
                      )}
                      <button
                        type="button"
                        aria-label={`Resize column ${colIndex + 1}`}
                        onPointerDown={(event) => onStartColumnResize(event, colIndex)}
                        onMouseEnter={() => onSetHoveredResizeCol(colIndex)}
                        onMouseLeave={() => {
                          if (!resizing) onSetHoveredResizeCol(null);
                        }}
                        className={cn(
                          "absolute -right-1 top-0 z-20 h-full w-2 cursor-col-resize touch-none transition",
                          hoveredResizeCol === colIndex || resizing?.colIndex === colIndex
                            ? "bg-blue-500"
                            : "bg-transparent",
                        )}
                      />
                    </Tag>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
